/** Ported from chief's `web/src/pages/Repositories.tsx` (MIT) — see "Licence and attribution" in README.md. */

import { type FormEvent, useEffect, useState } from 'react';

import {
  type BrandBookSummary,
  type ConnectionTestResult,
  createRepository,
  deleteRepository,
  fetchBrandBooks,
  fetchRepositoryList,
  type Repository,
  type RepositoryInput,
  testRepositoryConnection,
  updateRepository,
} from '../api.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { describeError, redirectIfUnauthorised, useAppData } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { brandBookPath, Link } from '../router.tsx';
import { useToast } from '../toast.tsx';
import {
  Badge,
  EmptyState,
  Markdown,
  Notice,
  PageHeader,
  Panel,
  Skeleton,
} from '../ui.tsx';

type TestState = { status: 'running' } | ({ status: 'done' } & ConnectionTestResult);

/**
 * Best-effort `owner/repo` preview, mirroring the server's derivation. It only
 * drives the placeholder — leaving the slug field empty makes the server derive
 * the real value, so the two can never disagree in the stored record.
 */
function deriveSlugPreview(raw: string): string | null {
  let value = raw.trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
  value = value.replace(/^[^@/:]+@/, '');
  const separator = value.search(/[:/]/);
  if (separator === -1) return null;
  let repoPath = value.slice(separator + 1);
  if (repoPath.toLowerCase().endsWith('.git')) repoPath = repoPath.slice(0, -'.git'.length);
  const segments = repoPath.split('/').filter((segment) => segment !== '');
  return segments.length === 2 ? segments.join('/') : null;
}

/**
 * Repositories (US-007, US-069): register a git remote, get a deploy key to
 * paste into the forge, prove the key works. The brand a repository yields
 * lives in a brand book (its own page); a row links to every book made from it.
 *
 * The deploy key promise is the server's sentence, fetched with the list
 * rather than retyped here: marketingchief only ever clones and fetches, so a
 * **read-only** key is enough, and a page that said otherwise would be asking
 * for a permission this server never uses.
 */
export function Repositories() {
  const { repositories, contents, refresh } = useAppData();
  const toast = useToast();
  const [help, setHelp] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Repository | null>(null);
  /** The repository added in this visit — its deploy key needs attention now. */
  const [addedId, setAddedId] = useState<string | null>(null);
  const [tests, setTests] = useState<Record<string, TestState>>({});
  /** The brand books made from a repository, by repository id. */
  const [books, setBooks] = useState<Map<string, BrandBookSummary[]>>(new Map());

  // The help sentence comes off the same endpoint as the list, but the shell's
  // poll keeps only the array; one read here is enough, as it never changes.
  useEffect(() => {
    const controller = new AbortController();
    fetchRepositoryList(controller.signal)
      .then((list) => setHelp(list.help))
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        redirectIfUnauthorised(cause);
      });
    return () => controller.abort();
  }, []);

  // Read again when a repository comes or goes (not on every poll of the
  // list); a failed read only costs the links.
  const repositoryIds = (repositories ?? []).map((repository) => repository.id).join(' ');
  useEffect(() => {
    const controller = new AbortController();
    fetchBrandBooks(controller.signal)
      .then((list) => {
        const byRepository = new Map<string, BrandBookSummary[]>();
        for (const book of list) {
          if (book.sourceType !== 'repository' || book.repositoryId === null) continue;
          byRepository.set(book.repositoryId, [...(byRepository.get(book.repositoryId) ?? []), book]);
        }
        setBooks(byRepository);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        redirectIfUnauthorised(cause);
      });
    return () => controller.abort();
  }, [repositoryIds]);

  const onCreate = (input: RepositoryInput): void => {
    setSaving(true);
    createRepository(input)
      .then(async (created) => {
        await refresh();
        setAdding(false);
        setAddedId(created.id);
        toast.ok(
          created.keySource === 'generated'
            ? `Added ${created.name}. Add its deploy key to the forge, read-only.`
            : `Added ${created.name} with the key you provided.`,
        );
      })
      .catch((cause: unknown) => toast.error(describeError(cause)))
      .finally(() => setSaving(false));
  };

  const onUpdate = (id: string, input: RepositoryInput): void => {
    setSaving(true);
    updateRepository(id, input)
      .then(async () => {
        await refresh();
        setEditingId(null);
        toast.ok('Repository saved.');
      })
      .catch((cause: unknown) => toast.error(describeError(cause)))
      .finally(() => setSaving(false));
  };

  const onDelete = (): void => {
    if (deleting === null) return;
    const repository = deleting;
    setDeleting(null);
    deleteRepository(repository.id)
      .then(async () => {
        await refresh();
        toast.ok(`Deleted ${repository.name}.`);
      })
      .catch((cause: unknown) => toast.error(describeError(cause)));
  };

  const onTest = (repository: Repository): void => {
    setTests((current) => ({ ...current, [repository.id]: { status: 'running' } }));
    testRepositoryConnection(repository.id)
      .then((result) =>
        setTests((current) => ({ ...current, [repository.id]: { status: 'done', ...result } })),
      )
      .catch((cause: unknown) =>
        setTests((current) => ({
          ...current,
          [repository.id]: {
            status: 'done',
            ok: false,
            message: describeError(cause),
            stdout: '',
            stderr: '',
          },
        })),
      );
  };

  const contentCount = (id: string): number =>
    (contents ?? []).filter((content) =>
      content.repositories.some((link) => link.repositoryId === id),
    ).length;

  return (
    <div className="page">
      <PageHeader
        title="Repositories"
        subtitle="The read-only clones every content draws its material from. The brand one yields lives in its brand book."
        actions={
          !adding && (
            <button type="button" className="button button--primary" onClick={() => setAdding(true)}>
              <Icon name="plus" />
              Add repository
            </button>
          )
        }
      />

      {help !== null && (
        <Notice kind="info">
          <Markdown text={help} />
        </Notice>
      )}

      {adding && (
        <RepositoryForm
          mode="create"
          busy={saving}
          onSubmit={onCreate}
          onCancel={() => setAdding(false)}
        />
      )}

      {repositories === null ? (
        <div className="panel">
          <div className="panel__body">
            <Skeleton lines={4} />
          </div>
        </div>
      ) : repositories.length === 0 && !adding ? (
        <EmptyState
          icon="repo"
          title="No repositories yet"
          action={
            <button type="button" className="button button--primary" onClick={() => setAdding(true)}>
              <Icon name="plus" />
              Add the first one
            </button>
          }
        >
          Register a repository by its SSH URL. marketingchief generates a deploy key for it and
          shows you the public half to add to the forge — read-only is enough.
        </EmptyState>
      ) : (
        <div className="stack">
          {repositories.map((repository) =>
            editingId === repository.id ? (
              <RepositoryForm
                key={repository.id}
                mode="edit"
                initial={repository}
                busy={saving}
                onSubmit={(input) => onUpdate(repository.id, input)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div className="stack stack--tight" key={repository.id}>
                <Panel
                  title={repository.name}
                  icon="repo"
                  meta={
                    repository.keyConfigured ? (
                      <Badge tone="done">key stored</Badge>
                    ) : (
                      <Badge tone="danger">no key</Badge>
                    )
                  }
                  actions={
                    <>
                      <button
                        type="button"
                        className="button button--small"
                        onClick={() => onTest(repository)}
                        disabled={
                          tests[repository.id]?.status === 'running' || !repository.keyConfigured
                        }
                      >
                        <Icon name="pulse" />
                        {tests[repository.id]?.status === 'running' ? 'Testing…' : 'Test connection'}
                      </button>
                      <button
                        type="button"
                        className="button button--small button--quiet"
                        onClick={() => setEditingId(repository.id)}
                      >
                        <Icon name="pencil" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="button button--small button--quiet button--danger button--icon"
                        onClick={() => setDeleting(repository)}
                        aria-label={`Delete ${repository.name}`}
                        title="Delete"
                      >
                        <Icon name="trash" />
                      </button>
                    </>
                  }
                >
                  <div className="repo-facts">
                    {repository.githubSlug !== null && (
                      <span className="repo-fact mono">
                        <Icon name="package" />
                        {repository.githubSlug}
                      </span>
                    )}
                    <span className="repo-fact mono">
                      <Icon name="git-branch" />
                      {repository.defaultBaseBranch}
                    </span>
                    <span className="repo-fact mono" title={repository.sshUrl}>
                      <Icon name="terminal" />
                      {repository.sshUrl}
                    </span>
                    <span
                      className="repo-fact mono"
                      title={
                        repository.keySource === null ? undefined : `${repository.keySource} key`
                      }
                    >
                      <Icon name="key" />
                      {repository.keyConfigured
                        ? (repository.keyFingerprint ?? 'stored')
                        : 'missing — edit and paste a private key'}
                    </span>
                    <span className="repo-fact">
                      <Icon name="rocket" />
                      <Link
                        className="link"
                        href={`/content?repository=${encodeURIComponent(repository.id)}`}
                      >
                        {contentCount(repository.id)}{' '}
                        {contentCount(repository.id) === 1 ? 'content' : 'contents'}
                      </Link>
                    </span>
                    {(books.get(repository.id) ?? []).map((book) => (
                      <span className="repo-fact" key={book.id}>
                        <Icon name="paintbrush" />
                        <Link className="link" href={brandBookPath(book.id)} title="Brand book">
                          {book.name}
                        </Link>
                      </span>
                    ))}
                  </div>

                  <DeployKey repository={repository} highlight={addedId === repository.id} />
                  <TestResult state={tests[repository.id]} />
                </Panel>
              </div>
            ),
          )}
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={deleting === null ? '' : `Delete ${deleting.name}?`}
        confirmLabel="Delete repository"
        danger
        onConfirm={onDelete}
        onCancel={() => setDeleting(null)}
      >
        <p>
          The repository and its SSH key are removed from this server. A brand book made from it
          keeps its profile but can no longer be refreshed. Nothing in the remote repository
          changes; remove the deploy key there yourself.
        </p>
        {deleting !== null && contentCount(deleting.id) > 0 && (
          <p>
            <strong>
              It is used by {contentCount(deleting.id)}{' '}
              {contentCount(deleting.id) === 1 ? 'content' : 'contents'}.
            </strong>{' '}
            The server refuses to delete a repository a content still draws on; delete them first.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}

function TestResult({ state }: { readonly state: TestState | undefined }) {
  if (state === undefined || state.status === 'running') return null;
  return (
    <div className="stack stack--tight">
      <Notice kind={state.ok ? 'ok' : 'error'}>{state.message}</Notice>
      {state.stderr !== '' && <pre className="output">{state.stderr}</pre>}
    </div>
  );
}

function DeployKey({
  repository,
  highlight,
}: {
  readonly repository: Repository;
  readonly highlight: boolean;
}) {
  if (repository.publicKey === null) {
    return (
      <p className="field__hint">
        The public half of this key is not stored: it was imported from a PEM private key. Use the
        public key you already have on the forge.
      </p>
    );
  }
  return (
    <details className="disclosure" open={highlight}>
      <summary className="disclosure__summary">
        <Icon name="chevron-right" />
        Deploy key
      </summary>
      <div className="disclosure__body">
        <p className="field__hint">
          Add this as a deploy key on the repository and leave <strong>Allow write access</strong>{' '}
          unchecked: marketingchief only ever clones and fetches.
        </p>
        <pre className="output output--wrap">{repository.publicKey}</pre>
        <CopyButton value={repository.publicKey} />
      </div>
    </details>
  );
}

function CopyButton({ value }: { readonly value: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = (): void => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setCopied(false));
  };
  return (
    <button type="button" className="button button--small" onClick={onCopy}>
      <Icon name={copied ? 'check' : 'copy'} />
      {copied ? 'Copied' : 'Copy public key'}
    </button>
  );
}

function RepositoryForm({
  mode,
  initial,
  busy,
  onSubmit,
  onCancel,
}: {
  readonly mode: 'create' | 'edit';
  readonly initial?: Repository;
  readonly busy: boolean;
  readonly onSubmit: (input: RepositoryInput) => void;
  readonly onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [sshUrl, setSshUrl] = useState(initial?.sshUrl ?? '');
  const [githubSlug, setGithubSlug] = useState(initial?.githubSlug ?? '');
  const [baseBranch, setBaseBranch] = useState(initial?.defaultBaseBranch ?? 'main');
  const [keyMode, setKeyMode] = useState<'generate' | 'paste'>('generate');
  const [privateKey, setPrivateKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const derived = deriveSlugPreview(sshUrl);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    if (name.trim() === '' || sshUrl.trim() === '') {
      setError('Name and SSH URL are required.');
      return;
    }
    if (keyMode === 'paste' && privateKey.trim() === '') {
      setError('Paste a private key, or switch back to generating one.');
      return;
    }
    const input: RepositoryInput = {
      name: name.trim(),
      sshUrl: sshUrl.trim(),
      defaultBaseBranch: baseBranch.trim() === '' ? 'main' : baseBranch.trim(),
    };
    if (githubSlug.trim() !== '') input.githubSlug = githubSlug.trim();
    if (keyMode === 'paste') input.privateKey = privateKey;
    onSubmit(input);
  };

  return (
    <form className="panel" onSubmit={submit}>
      <header className="panel__header">
        <h2 className="panel__title">
          <Icon name={mode === 'create' ? 'plus' : 'pencil'} />
          {mode === 'create' ? 'Add repository' : `Edit ${initial?.name ?? ''}`}
        </h2>
      </header>
      <div className="panel__body form">
        <div className="field__row">
          <div className="field">
            <label className="field__label" htmlFor={`name-${mode}`}>
              Name
            </label>
            <input
              id={`name-${mode}`}
              className="field__input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="my-app"
              autoComplete="off"
            />
            <p className="field__hint">How it appears in marketingchief.</p>
          </div>
          <div className="field">
            <label className="field__label" htmlFor={`branch-${mode}`}>
              Default branch
            </label>
            <input
              id={`branch-${mode}`}
              className="field__input"
              value={baseBranch}
              onChange={(event) => setBaseBranch(event.target.value)}
              placeholder="main"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="field__hint">What a clone and a brand extraction read.</p>
          </div>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={`ssh-url-${mode}`}>
            SSH URL
          </label>
          <input
            id={`ssh-url-${mode}`}
            className="field__input mono"
            value={sshUrl}
            onChange={(event) => setSshUrl(event.target.value)}
            placeholder="git@github.com:owner/repo.git"
            autoComplete="off"
            spellCheck={false}
          />
          <p className="field__hint">SSH, not HTTPS: the deploy key is what authenticates.</p>
        </div>

        <div className="field">
          <label className="field__label" htmlFor={`slug-${mode}`}>
            Slug <span className="muted">(optional)</span>
          </label>
          <input
            id={`slug-${mode}`}
            className="field__input mono"
            value={githubSlug}
            onChange={(event) => setGithubSlug(event.target.value)}
            placeholder={derived ?? 'owner/repo'}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="field__hint">
            A label only. Leave it blank to derive it from the URL
            {derived === null ? '.' : ` (${derived}).`}
          </p>
        </div>

        <div className="field">
          <span className="field__label">SSH key</span>
          <label className="radio">
            <input
              type="radio"
              name={`key-mode-${mode}`}
              checked={keyMode === 'generate'}
              onChange={() => setKeyMode('generate')}
            />
            {mode === 'create' ? 'Generate a new ed25519 keypair' : 'Keep the stored key'}
          </label>
          <label className="radio">
            <input
              type="radio"
              name={`key-mode-${mode}`}
              checked={keyMode === 'paste'}
              onChange={() => setKeyMode('paste')}
            />
            Paste an existing private key
          </label>
          {keyMode === 'paste' && (
            <textarea
              className="field__input field__textarea"
              value={privateKey}
              onChange={(event) => setPrivateKey(event.target.value)}
              placeholder={'-----BEGIN OPENSSH PRIVATE KEY-----\n…'}
              rows={6}
              spellCheck={false}
              aria-label="Private key"
            />
          )}
          <p className="field__hint">
            {mode === 'create'
              ? 'The public half is shown after saving, to add as a read-only deploy key. A pasted key must be unencrypted.'
              : 'The stored key is kept unless you paste a replacement.'}
          </p>
        </div>

        {error !== null && <Notice kind="error">{error}</Notice>}

        <div className="field__actions">
          <button type="submit" className="button button--primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Add repository' : 'Save'}
          </button>
          <button type="button" className="button button--quiet" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}
